
import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import ModalOverlay from '../ModalOverlay/ModalOverlay';
import Button from '../Buttons/Button/Button';
import { copyToClipboard } from '@/utils/shareUtils';
import './bookingPopupLayer.css';

interface BookingSsoBlockedModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Outbound URL when pop-up was blocked. Enables primary open + copy actions. */
  loginUrl?: string;
  /** `booking` = Nuitee/hotel SSO (default). `gyg` = GetYourGuide. `stay22` = Stay22 Allez outbound. */
  variant?: 'booking' | 'gyg' | 'stay22';
}

const COPY = {
  booking: {
    title: "We couldn't open the booking page",
    shortcuts: 'Or use one of these shortcuts to open your booking now:',
    openPrimary: 'Open booking',
    tryAgainStep: 'Try again',
    stillBlockedToast: 'Pop-ups are still blocked. Please use "Copy link" and paste it in a new tab.',
  },
  gyg: {
    title: "We couldn't open GetYourGuide",
    shortcuts: 'Or use one of these shortcuts to open GetYourGuide now:',
    openPrimary: 'Open GetYourGuide',
    tryAgainStep: 'Open GetYourGuide',
    stillBlockedToast: 'Pop-ups are still blocked. Please use "Copy link" and paste it in a new tab.',
  },
  stay22: {
    title: "We couldn't open Stay22",
    shortcuts: 'Or use one of these shortcuts to open Stay22 now:',
    openPrimary: 'Open Stay22',
    tryAgainStep: 'Open Stay22',
    stillBlockedToast: 'Pop-ups are still blocked. Please use "Copy link" and paste it in a new tab.',
  },
} as const;

/**
 * Shown when window.open() returns null, indicating the browser's popup blocker
 * prevented the new tab from opening. Replaces the silent "nothing happens" UX.
 *
 * When loginUrl is available, offers open-on-gesture and copy-link fallbacks.
 */
const BookingSsoBlockedModal: React.FC<BookingSsoBlockedModalProps> = ({
  isOpen,
  onClose,
  loginUrl,
  variant = 'booking',
}) => {
  const copy = COPY[variant];
  const [isCopied, setIsCopied] = useState(false);

  const handleOpenBooking = useCallback(() => {
    if (!loginUrl) return;
    const opened = window.open(loginUrl, '_blank', 'noopener,noreferrer');
    if (opened) {
      onClose();
    } else {
      toast.error(copy.stillBlockedToast);
    }
  }, [loginUrl, onClose, copy.stillBlockedToast]);

  const handleCopyLink = useCallback(async () => {
    if (!loginUrl) return;
    try {
      await copyToClipboard(loginUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    } catch {
      toast.error('Unable to copy. Please allow pop-ups for this site and try again.');
    }
  }, [loginUrl]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClose={onClose} className="booking-popup-overlay">
      <div className="flex flex-col items-center gap-4 bg-white rounded-2xl max-w-[480px] w-[calc(100vw-32px)] p-8 text-center font-manrope">
        <div className="text-4xl leading-none" aria-hidden="true">🚫</div>

        <h2 className="m-0 text-xl font-bold text-gray-900">
          {copy.title}
        </h2>

        <p className="m-0 text-[15px] text-gray-500 leading-relaxed">
          Your browser blocked the new tab from opening. This is usually caused by
          a pop-up blocker.
        </p>

        <div className="bg-gray-50 rounded-xl p-4 text-left w-full box-border">
          <p className="m-0 mb-2 text-[13px] font-semibold text-gray-700 uppercase tracking-wide">
            To fix this:
          </p>
          <ol className="m-0 pl-5 flex flex-col gap-1.5">
            <li className="text-sm text-gray-700 leading-snug">
              Look for a <strong>pop-up blocked</strong> icon in your browser&apos;s
              address bar (usually on the right).
            </li>
            <li className="text-sm text-gray-700 leading-snug">
              Click it and choose <strong>Always allow pop-ups from this site</strong>.
            </li>
            <li className="text-sm text-gray-700 leading-snug">
              Then click <strong>{copy.tryAgainStep}</strong> below.
            </li>
          </ol>
        </div>

        {loginUrl && (
          <p className="m-0 text-sm text-gray-500">
            {copy.shortcuts}
          </p>
        )}

        <div className="flex flex-wrap gap-2.5 justify-center w-full mt-1">
          {loginUrl && (
            <>
              <Button variant="primary" size="md" onClick={handleOpenBooking}>
                {copy.openPrimary}
              </Button>
              <Button
                variant="secondary"
                size="md"
                onClick={handleCopyLink}
                disabled={isCopied}
              >
                {isCopied ? 'Copied!' : 'Copy link'}
              </Button>
            </>
          )}
          <Button
            variant={loginUrl ? 'secondary' : 'primary'}
            size="md"
            onClick={onClose}
          >
            {loginUrl ? 'Dismiss' : 'Close'}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
};

export default BookingSsoBlockedModal;
